import { useMemo, useState, useEffect } from 'react';

import { useFilterService } from '../../providers/filter/filter.context';
import {
  hasError,
  isFilterApplicable,
  compareFilters,
  areFilterArraysEqual,
  mergeFilters,
  isEmptyFilterValue,
} from '../../providers/filter/filter.utils';
import { Filter, FilterIDs } from '../../providers/filter/filter.types';

export function useFilters(filterID: FilterIDs) {
  const filterService = useFilterService();
  const filters = useMemo(() => {
    return filterService.getFilters(filterID);
  }, [filterID, filterService]);
  const [filtersCopy, setFiltersCopy] = useState<Filter[]>([...filters]);
  const [prevFilters, setPrevFilters] = useState<Filter[]>(filters);

  const canApply = useMemo(() => {
    if (filtersCopy.some(hasError)) return false;
    const validFilters = filtersCopy.filter(isFilterApplicable);
    return !areFilterArraysEqual(validFilters, filters);
  }, [filtersCopy, filters]);

  const hasPendingChanges = useMemo(() => {
    return !areFilterArraysEqual(filtersCopy, filters);
  }, [filtersCopy, filters]);

  const handleApply = () => {
    if (!canApply) return;
    const changedFilters = filtersCopy.filter((filter) => {
      if (!isFilterApplicable(filter)) return false;
      const origFilter = filters.find((f) => f.name === filter.name);
      return (
        !origFilter || !compareFilters(filter, origFilter) || filter.active !== origFilter.active
      );
    });
    const removedFilters = filters
      .filter((f) => !filtersCopy.some((g) => g.name === f.name))
      .map((f) => {
        const g = f.copy();
        g.value = [];
        return g;
      });
    const curFilters = [...changedFilters, ...removedFilters];
    filterService.updateFilters(filterID, curFilters);
  };

  const handleClear = () => {
    if (filters.length === 0) {
      setFiltersCopy([]);
      return;
    }

    setFiltersCopy([]);
  };

  const handleChange = (ft: Filter) => {
    const newFilters = [...filtersCopy];
    const filterIndex = newFilters.findIndex((f) => f.name === ft.name);
    const foundFilter = newFilters[filterIndex];
    const newFilter = foundFilter ? mergeFilters(foundFilter, ft) : ft;
    const isEmpty = isEmptyFilterValue(newFilter.value);
    if (isEmpty && foundFilter) {
      return setFiltersCopy(newFilters.filter((old) => old !== foundFilter));
    }
    if (!foundFilter && newFilter.isDefaultValue()) return;
    if (!foundFilter) newFilters.push(newFilter);
    else newFilters[filterIndex] = newFilter;
    setFiltersCopy(newFilters);
  };

  useEffect(() => {
    if (areFilterArraysEqual(filters, prevFilters)) return;
    setPrevFilters(filters);
    setFiltersCopy(filters.map((f) => f.copy()));
  }, [filters, prevFilters]);

  return {
    hasPendingChanges,
    handleChange,
    handleClear,
    handleApply,
    filtersCopy,
    filters,
    canApply,
  };
}
